import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import FeatureIcon from '../../components/FeatureIcon';
import { isAndroidSummaryStat } from './statusData';

interface BetaGuidanceProps {
  betaStatus: any;
  latestIosVersion: string | null;
  latestAndroidVersion: string | null;
  loading: boolean;
}

const BetaGuidance: React.FC<BetaGuidanceProps> = ({
  betaStatus,
  latestIosVersion,
  latestAndroidVersion,
  loading,
}) => {
  const { colors } = useTheme();
  // Beta guide reporting sections — collapsible accordion. First section
  // (iOS TestFlight, the preferred method) is expanded by default.
  const [expandedReporting, setExpandedReporting] = useState<Set<number>>(new Set([0]));

  if (betaStatus?.visible === false) return null;

  return (
    <section aria-labelledby="status-beta-heading" className="status-beta status-panel status-beta-panel">
      <h2
        id="status-beta-heading"
        className="status-panel-heading"
        style={{ color: colors.textPrimary }}
      >
        <FeatureIcon name="chat" size={20} className="status-panel-icon" />
        {betaStatus?.title || 'Beta Testing Guide'}
      </h2>
      <p className="text-sm mb-5" style={{ color: colors.textSecondary }}>
        {betaStatus?.subtitle || 'Essential tips for effective testing and feedback'}
      </p>

      {/* Summary stats stay visible near the top, before disclosures */}
      {(betaStatus?.stats || []).length > 0 && (
        <div className="status-beta-stats mb-5">
          {betaStatus.stats.map((stat: any, i: number) => (
            <div key={i} className="status-stat" style={{ borderColor: colors.border }}>
              <div
                className="text-xl font-bold mb-1 whitespace-pre-line leading-tight"
                style={{ color: colors.textPrimary }}
              >
                {stat.value_type === 'auto_build'
                  ? (latestIosVersion || (loading ? '...' : 'N/A'))
                  : isAndroidSummaryStat(stat)
                    ? (latestAndroidVersion || (loading ? '...' : 'N/A'))
                    : stat.value}
              </div>
              <div className="text-sm" style={{ color: colors.textSecondary }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tips — neutral disclosure, closed by default */}
      <details className="status-beta-details" style={{ borderColor: colors.border }}>
        <summary
          className="status-beta-summary"
          style={{ color: colors.textPrimary }}
        >
          {betaStatus?.tips_header || 'Testing Best Practices'}
        </summary>
        <ol className="status-tip-list">
          {(betaStatus?.tips || []).map((tip: any, i: number) => (
            <li key={i} className="status-tip" style={{ borderColor: colors.border }}>
              <div className="font-semibold mb-1" style={{ color: colors.textPrimary }}>
                {tip.title}
              </div>
              <div className="text-sm" style={{ color: colors.textSecondary }}>
                {tip.description}
              </div>
            </li>
          ))}
        </ol>
      </details>

      {/* Reporting — collapsible accordion, first section expanded */}
      <h3 className="text-lg font-semibold mt-6 mb-3" style={{ color: colors.textPrimary }}>
        {betaStatus?.reporting_header || 'How to Report Issues'}
      </h3>
      <div className="space-y-3">
        {(betaStatus?.reporting || []).map((r: any, i: number) => {
          const panelId = `beta-reporting-panel-${i}`;
          const buttonId = `beta-reporting-button-${i}`;
          const isExpanded = expandedReporting.has(i);
          const hasItems = r.items && r.items.length > 0;
          return (
            <div
              key={i}
              className="status-reporting-item"
              style={{ borderColor: colors.border }}
            >
              <button
                id={buttonId}
                type="button"
                aria-expanded={isExpanded}
                aria-controls={hasItems ? panelId : undefined}
                onClick={() => {
                  setExpandedReporting(prev => {
                    const next = new Set(prev);
                    if (next.has(i)) next.delete(i);
                    else next.add(i);
                    return next;
                  });
                }}
                className="w-full flex items-center justify-between gap-3 p-4 text-left font-semibold"
                style={{ color: colors.textPrimary }}
              >
                <span>{r.title}</span>
                {hasItems && (
                  <svg
                    className="w-5 h-5 flex-shrink-0 transition-transform duration-200"
                    style={{ color: colors.textSecondary, transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </button>
              {r.description && (
                <div className="px-4 pb-2 text-sm" style={{ color: colors.textSecondary }}>
                  {r.description}
                </div>
              )}
              {hasItems && (
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={buttonId}
                  hidden={!isExpanded}
                  className="px-4 pb-4"
                >
                  <ul className="text-sm space-y-1 ml-2" style={{ color: colors.textSecondary }}>
                    {r.items.map((item: string, j: number) => (
                      <li key={j} className="list-disc ml-4">{item}</li>
                    ))}
                  </ul>
                  {r.link_url && (
                    <a
                      href={r.link_url}
                      className="status-feedback-link"
                      style={{ color: colors.textPrimary, borderColor: colors.border }}
                    >
                      {r.link_text || 'Learn More'}
                    </a>
                  )}
                </div>
              )}
              {!hasItems && r.link_url && (
                <div className="px-4 pb-4">
                  <a
                    href={r.link_url}
                    className="status-feedback-link"
                    style={{ color: colors.textPrimary, borderColor: colors.border }}
                  >
                    {r.link_text || 'Learn More'}
                  </a>
                </div>
              )}
            </div>
          );
        })}
      </div>

    </section>
  );
};

export default BetaGuidance;
